import * as THREE from 'three';
import { EFFECT_LAYER } from '../enhanced-effects';

// ---------------------------------------------------------------------------
// Weather System — rain, snow, fog with smooth transitions
// ---------------------------------------------------------------------------

export enum WeatherType {
  Clear = 'clear',
  Rain = 'rain',
  Snow = 'snow',
  Fog = 'fog',
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RAIN_PARTICLE_COUNT = 2000;
const SNOW_PARTICLE_COUNT = 1500;
const RAIN_SPEED_MIN = 15; // m/s
const RAIN_SPEED_MAX = 25; // m/s
const SNOW_SPEED_MIN = 1; // m/s
const SNOW_SPEED_MAX = 3; // m/s
const TRANSITION_DURATION = 2.0; // seconds
const SPAWN_BOX = new THREE.Vector3(60, 40, 60); // metres
const GROUND_Y = 0;
const FOG_WEATHER_DENSITY = 0.05; // FogExp2 density for fog weather (~30-50m visibility)
const FOG_WEATHER_GREY = new THREE.Color(0x999999); // shared tint, avoids per-frame allocation
const SNOW_DRIFT_AMPLITUDE = 1.5; // metres of horizontal drift
const SNOW_DRIFT_FREQUENCY = 0.8; // oscillation speed

// Splash pool size
const SPLASH_POOL_SIZE = 100;
const SPLASH_LIFETIME = 0.15; // seconds

// ---------------------------------------------------------------------------
// Particle state types
// ---------------------------------------------------------------------------

interface ParticleState {
  positions: Float32Array;
  velocities: Float32Array;
  count: number;
  geometry: THREE.BufferGeometry;
  material: THREE.ShaderMaterial;
  points: THREE.Points;
  /** Per-particle random phase for snow drift */
  phases?: Float32Array;
  /** Per-particle random drift scale */
  driftScales?: Float32Array;
}

interface SplashState {
  positions: Float32Array;
  lifetimes: Float32Array;
  geometry: THREE.BufferGeometry;
  material: THREE.ShaderMaterial;
  points: THREE.Points;
  activeCount: number;
  nextIndex: number;
}

// ---------------------------------------------------------------------------
// WeatherSystem
// ---------------------------------------------------------------------------

export class WeatherSystem {
  private scene: THREE.Scene;
  private _currentType: WeatherType = WeatherType.Clear;
  private _targetType: WeatherType = WeatherType.Clear;
  private _intensity = 0;
  private _windDirection = new THREE.Vector3(1, 0, 0.3);
  private transitionDir: 1 | -1 = 1; // 1 = fading in, -1 = fading out
  private transitionPending: WeatherType | null = null;
  private _disposed = false;

  // Active particle systems
  private rainState: ParticleState | null = null;
  private snowState: ParticleState | null = null;
  private splashState: SplashState | null = null;

  // Fog bookkeeping
  private originalFogDensity = 0.006;
  private originalFogColor = new THREE.Color(0xc8bfa0);

  // Snow time accumulator for sin-wave drift
  private snowTime = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    // Capture original fog settings
    if (scene.fog instanceof THREE.FogExp2) {
      this.originalFogDensity = scene.fog.density;
      this.originalFogColor.copy(scene.fog.color);
    }
  }

  // ── Public getters ──────────────────────────────────────────────────────

  get currentType(): WeatherType {
    return this._currentType;
  }

  get targetType(): WeatherType {
    return this._targetType;
  }

  get intensity(): number {
    return this._intensity;
  }

  get windDirection(): THREE.Vector3 {
    return this._windDirection;
  }

  get activeParticleCount(): number {
    if (this._currentType === WeatherType.Rain && this.rainState) {
      return this.rainState.count;
    }
    if (this._currentType === WeatherType.Snow && this.snowState) {
      return this.snowState.count;
    }
    return 0;
  }

  // ── Public API ──────────────────────────────────────────────────────────

  setWeather(type: WeatherType): void {
    if (this._disposed) return;
    this._targetType = type;

    if (this._currentType === WeatherType.Clear && type !== WeatherType.Clear) {
      // Fade in directly
      this._currentType = type;
      this.transitionDir = 1;
      this.transitionPending = null;
      this.ensureParticleSystem(type);
    } else if (type === WeatherType.Clear && this._currentType !== WeatherType.Clear) {
      // Fade out current
      this.transitionDir = -1;
      this.transitionPending = null;
    } else if (type !== this._currentType) {
      // Switching between two non-clear types: fade out first, then swap
      this.transitionDir = -1;
      this.transitionPending = type;
    }
    // If type === currentType, no-op
  }

  setWindDirection(wind: THREE.Vector3): void {
    this._windDirection.copy(wind);
    // Rebuild rain velocities if rain is active
    if (this.rainState) {
      this.initRainVelocities(this.rainState);
    }
  }

  update(dt: number, cameraPosition: THREE.Vector3): void {
    if (this._disposed) return;

    // ── Transition logic ──────────────────────────────────────────────
    const transitionSpeed = 1.0 / TRANSITION_DURATION;

    if (this.transitionDir === 1 && this._intensity < 1) {
      this._intensity = Math.min(1, this._intensity + transitionSpeed * dt);
    } else if (this.transitionDir === -1 && this._intensity > 0) {
      this._intensity = Math.max(0, this._intensity - transitionSpeed * dt);
      if (this._intensity <= 0) {
        // Fade-out complete
        this.destroyActiveParticles();
        if (this.transitionPending !== null) {
          // Switch to the pending weather type and fade in
          this._currentType = this.transitionPending;
          this.transitionPending = null;
          this.transitionDir = 1;
          this.ensureParticleSystem(this._currentType);
        } else {
          this._currentType = WeatherType.Clear;
        }
      }
    }

    // ── Update active systems ─────────────────────────────────────────
    if (this._currentType === WeatherType.Rain && this.rainState) {
      this.updateRain(dt, cameraPosition);
      this.updateSplashes(dt);
    }
    if (this._currentType === WeatherType.Snow && this.snowState) {
      this.snowTime += dt;
      this.updateSnow(dt, cameraPosition);
    }
    if (this._currentType === WeatherType.Fog || this._targetType === WeatherType.Fog) {
      this.updateFog();
    } else if (this._targetType === WeatherType.Clear) {
      // Restore fog when transitioning back to clear
      this.updateFog();
    }
  }

  /** Get current particle positions (for testing). */
  getParticlePositions(): Float32Array | null {
    const state = this.rainState ?? this.snowState;
    if (!state) return null;
    return state.positions;
  }

  /** Get current particle velocities (for testing). */
  getParticleVelocities(): Float32Array | null {
    const state = this.rainState ?? this.snowState;
    if (!state) return null;
    return state.velocities;
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    this.destroyActiveParticles();
    // Restore fog
    if (this.scene.fog instanceof THREE.FogExp2) {
      this.scene.fog.density = this.originalFogDensity;
      this.scene.fog.color.copy(this.originalFogColor);
    }
  }

  // ── Private: particle system lifecycle ──────────────────────────────

  private ensureParticleSystem(type: WeatherType): void {
    if (type === WeatherType.Rain && !this.rainState) {
      this.rainState = this.createRainSystem();
      this.splashState = this.createSplashSystem();
    }
    if (type === WeatherType.Snow && !this.snowState) {
      this.snowState = this.createSnowSystem();
    }
    // Fog doesn't need particles — it adjusts scene.fog
  }

  private destroyActiveParticles(): void {
    if (this.rainState) {
      this.scene.remove(this.rainState.points);
      this.rainState.geometry.dispose();
      this.rainState.material.dispose();
      this.rainState = null;
    }
    if (this.splashState) {
      this.scene.remove(this.splashState.points);
      this.splashState.geometry.dispose();
      this.splashState.material.dispose();
      this.splashState = null;
    }
    if (this.snowState) {
      this.scene.remove(this.snowState.points);
      this.snowState.geometry.dispose();
      this.snowState.material.dispose();
      this.snowState = null;
    }
  }

  // ── Rain ────────────────────────────────────────────────────────────

  private createRainSystem(): ParticleState {
    const count = RAIN_PARTICLE_COUNT;
    const positions = new Float32Array(count * 3);
    const velocities = new Float32Array(count * 3);

    // Random initial positions within spawn box
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * SPAWN_BOX.x;
      positions[i * 3 + 1] = Math.random() * SPAWN_BOX.y + GROUND_Y;
      positions[i * 3 + 2] = (Math.random() - 0.5) * SPAWN_BOX.z;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uIntensity: { value: 0 },
        uPixelRatio: { value: typeof window !== 'undefined' ? Math.min(window.devicePixelRatio, 2) : 1 },
      },
      vertexShader: /* glsl */ `
        uniform float uPixelRatio;
        varying float vAlpha;
        void main() {
          vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
          // Stretched rain drops — taller point size
          gl_PointSize = 2.0 * uPixelRatio * (200.0 / -mvPos.z);
          gl_PointSize = clamp(gl_PointSize, 1.0, 8.0);
          gl_Position = projectionMatrix * mvPos;
          // Fade with distance
          vAlpha = smoothstep(200.0, 20.0, -mvPos.z);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uIntensity;
        varying float vAlpha;
        void main() {
          // Elongated raindrop shape
          vec2 uv = gl_PointCoord - 0.5;
          float d = length(vec2(uv.x * 2.0, uv.y)) * 2.0;
          float alpha = smoothstep(1.0, 0.2, d) * 0.6 * vAlpha * uIntensity;
          gl_FragColor = vec4(0.7, 0.75, 0.85, alpha);
        }
      `,
    });

    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    points.layers.set(EFFECT_LAYER);
    points.userData.weatherSystem = true;
    this.scene.add(points);

    const state: ParticleState = { positions, velocities, count, geometry, material, points };
    this.initRainVelocities(state);
    return state;
  }

  private initRainVelocities(state: ParticleState): void {
    for (let i = 0; i < state.count; i++) {
      const speed = RAIN_SPEED_MIN + Math.random() * (RAIN_SPEED_MAX - RAIN_SPEED_MIN);
      // Wind influence on X and Z
      state.velocities[i * 3] = this._windDirection.x * 2 + (Math.random() - 0.5) * 0.5;
      state.velocities[i * 3 + 1] = -speed; // downward
      state.velocities[i * 3 + 2] = this._windDirection.z * 1.5 + (Math.random() - 0.5) * 0.3;
    }
  }

  private updateRain(dt: number, camPos: THREE.Vector3): void {
    const state = this.rainState!;
    const pos = state.positions;
    const vel = state.velocities;
    const halfX = SPAWN_BOX.x * 0.5;
    const halfZ = SPAWN_BOX.z * 0.5;
    const topY = SPAWN_BOX.y;

    for (let i = 0; i < state.count; i++) {
      const ix = i * 3, iy = i * 3 + 1, iz = i * 3 + 2;
      pos[ix] += vel[ix] * dt;
      pos[iy] += vel[iy] * dt;
      pos[iz] += vel[iz] * dt;

      // Ground hit → spawn splash, recycle to top
      if (pos[iy] < GROUND_Y) {
        this.spawnSplash(pos[ix] + camPos.x, GROUND_Y, pos[iz] + camPos.z);
        pos[iy] = topY + Math.random() * 5;
        pos[ix] = (Math.random() - 0.5) * SPAWN_BOX.x;
        pos[iz] = (Math.random() - 0.5) * SPAWN_BOX.z;
      }

      // Wrap horizontally around camera
      const relX = pos[ix] - camPos.x;
      const relZ = pos[iz] - camPos.z;
      if (relX > halfX) pos[ix] -= SPAWN_BOX.x;
      else if (relX < -halfX) pos[ix] += SPAWN_BOX.x;
      if (relZ > halfZ) pos[iz] -= SPAWN_BOX.z;
      else if (relZ < -halfZ) pos[iz] += SPAWN_BOX.z;
    }

    const posAttr = state.geometry.getAttribute('position') as THREE.BufferAttribute;
    posAttr.needsUpdate = true;
    (state.material.uniforms.uIntensity as { value: number }).value = this._intensity;
  }

  // ── Splash particles ────────────────────────────────────────────────

  private createSplashSystem(): SplashState {
    const positions = new Float32Array(SPLASH_POOL_SIZE * 3);
    const lifetimes = new Float32Array(SPLASH_POOL_SIZE);

    // Init all off-screen
    for (let i = 0; i < SPLASH_POOL_SIZE; i++) {
      positions[i * 3 + 1] = -1000;
      lifetimes[i] = 0;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aLife', new THREE.BufferAttribute(lifetimes, 1));

    const material = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uPixelRatio: { value: typeof window !== 'undefined' ? Math.min(window.devicePixelRatio, 2) : 1 },
      },
      vertexShader: /* glsl */ `
        attribute float aLife;
        uniform float uPixelRatio;
        varying float vLife;
        void main() {
          vLife = aLife;
          vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = mix(4.0, 1.0, 1.0 - aLife) * uPixelRatio * (150.0 / -mvPos.z);
          gl_PointSize = clamp(gl_PointSize, 1.0, 6.0);
          gl_Position = projectionMatrix * mvPos;
        }
      `,
      fragmentShader: /* glsl */ `
        varying float vLife;
        void main() {
          float d = length(gl_PointCoord - 0.5) * 2.0;
          float alpha = smoothstep(1.0, 0.3, d) * vLife * 0.5;
          gl_FragColor = vec4(0.8, 0.85, 0.95, alpha);
        }
      `,
    });

    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    points.layers.set(EFFECT_LAYER);
    points.userData.weatherSystem = true;
    this.scene.add(points);

    return { positions, lifetimes, geometry, material, points, activeCount: 0, nextIndex: 0 };
  }

  private spawnSplash(wx: number, wy: number, wz: number): void {
    if (!this.splashState) return;
    const s = this.splashState;
    const idx = s.nextIndex;
    s.positions[idx * 3] = wx;
    s.positions[idx * 3 + 1] = wy + 0.05;
    s.positions[idx * 3 + 2] = wz;
    s.lifetimes[idx] = 1.0;
    s.nextIndex = (s.nextIndex + 1) % SPLASH_POOL_SIZE;
  }

  private updateSplashes(dt: number): void {
    if (!this.splashState) return;
    const s = this.splashState;
    const decayRate = 1.0 / SPLASH_LIFETIME;
    for (let i = 0; i < SPLASH_POOL_SIZE; i++) {
      if (s.lifetimes[i] > 0) {
        s.lifetimes[i] = Math.max(0, s.lifetimes[i] - decayRate * dt);
        if (s.lifetimes[i] <= 0) {
          s.positions[i * 3 + 1] = -1000; // hide
        }
      }
    }
    (s.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (s.geometry.getAttribute('aLife') as THREE.BufferAttribute).needsUpdate = true;
  }

  // ── Snow ────────────────────────────────────────────────────────────

  private createSnowSystem(): ParticleState {
    const count = SNOW_PARTICLE_COUNT;
    const positions = new Float32Array(count * 3);
    const velocities = new Float32Array(count * 3);
    const phases = new Float32Array(count);
    const driftScales = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * SPAWN_BOX.x;
      positions[i * 3 + 1] = Math.random() * SPAWN_BOX.y + GROUND_Y;
      positions[i * 3 + 2] = (Math.random() - 0.5) * SPAWN_BOX.z;

      const speed = SNOW_SPEED_MIN + Math.random() * (SNOW_SPEED_MAX - SNOW_SPEED_MIN);
      velocities[i * 3] = 0; // X drift handled by sin wave
      velocities[i * 3 + 1] = -speed;
      velocities[i * 3 + 2] = 0;

      phases[i] = Math.random() * Math.PI * 2;
      driftScales[i] = 0.5 + Math.random() * 1.0;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
      uniforms: {
        uIntensity: { value: 0 },
        uPixelRatio: { value: typeof window !== 'undefined' ? Math.min(window.devicePixelRatio, 2) : 1 },
      },
      vertexShader: /* glsl */ `
        uniform float uPixelRatio;
        varying float vAlpha;
        void main() {
          vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = 3.0 * uPixelRatio * (200.0 / -mvPos.z);
          gl_PointSize = clamp(gl_PointSize, 2.0, 10.0);
          gl_Position = projectionMatrix * mvPos;
          vAlpha = smoothstep(200.0, 20.0, -mvPos.z);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uIntensity;
        varying float vAlpha;
        void main() {
          float d = length(gl_PointCoord - 0.5) * 2.0;
          float alpha = smoothstep(1.0, 0.1, d) * 0.8 * vAlpha * uIntensity;
          gl_FragColor = vec4(0.95, 0.97, 1.0, alpha);
        }
      `,
    });

    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    points.layers.set(EFFECT_LAYER);
    points.userData.weatherSystem = true;
    this.scene.add(points);

    return { positions, velocities, count, geometry, material, points, phases, driftScales };
  }

  private updateSnow(dt: number, camPos: THREE.Vector3): void {
    const state = this.snowState!;
    const pos = state.positions;
    const vel = state.velocities;
    const phases = state.phases!;
    const driftScales = state.driftScales!;
    const halfX = SPAWN_BOX.x * 0.5;
    const halfZ = SPAWN_BOX.z * 0.5;
    const topY = SPAWN_BOX.y;

    for (let i = 0; i < state.count; i++) {
      const ix = i * 3, iy = i * 3 + 1, iz = i * 3 + 2;

      // Vertical fall
      pos[iy] += vel[iy] * dt;

      // Sin-wave horizontal drift
      const drift = Math.sin(this.snowTime * SNOW_DRIFT_FREQUENCY + phases[i]) * SNOW_DRIFT_AMPLITUDE * driftScales[i];
      pos[ix] += drift * dt;
      pos[iz] += Math.cos(this.snowTime * SNOW_DRIFT_FREQUENCY * 0.7 + phases[i]) * SNOW_DRIFT_AMPLITUDE * 0.5 * driftScales[i] * dt;

      // Ground hit → recycle (no splash for snow)
      if (pos[iy] < GROUND_Y) {
        pos[iy] = topY + Math.random() * 5;
        pos[ix] = (Math.random() - 0.5) * SPAWN_BOX.x;
        pos[iz] = (Math.random() - 0.5) * SPAWN_BOX.z;
      }

      // Wrap horizontally around camera
      const relX = pos[ix] - camPos.x;
      const relZ = pos[iz] - camPos.z;
      if (relX > halfX) pos[ix] -= SPAWN_BOX.x;
      else if (relX < -halfX) pos[ix] += SPAWN_BOX.x;
      if (relZ > halfZ) pos[iz] -= SPAWN_BOX.z;
      else if (relZ < -halfZ) pos[iz] += SPAWN_BOX.z;
    }

    const posAttr = state.geometry.getAttribute('position') as THREE.BufferAttribute;
    posAttr.needsUpdate = true;
    (state.material.uniforms.uIntensity as { value: number }).value = this._intensity;
  }

  // ── Fog ─────────────────────────────────────────────────────────────

  private updateFog(): void {
    const fog = this.scene.fog;
    if (!(fog instanceof THREE.FogExp2)) return;

    if (this._currentType === WeatherType.Fog) {
      // Lerp density toward weather fog density based on intensity
      const targetDensity = THREE.MathUtils.lerp(this.originalFogDensity, FOG_WEATHER_DENSITY, this._intensity);
      fog.density = targetDensity;
      // Slightly shift fog color toward grey (FOG_WEATHER_GREY is a shared constant)
      fog.color.copy(this.originalFogColor).lerp(FOG_WEATHER_GREY, this._intensity * 0.3);
    } else {
      // Restore toward original
      const t = 1 - this._intensity; // as intensity drops, restore more
      if (this._targetType === WeatherType.Clear) {
        fog.density = THREE.MathUtils.lerp(fog.density, this.originalFogDensity, Math.min(1, t + 0.02));
        fog.color.lerp(this.originalFogColor, Math.min(1, t + 0.02));
      }
    }
  }
}
